import React, { useEffect, useState } from 'react';
import { formatCompactCurrency } from '../utils/formatters';
import api from '../services/api';

interface Lead {
  id: string;
  company: string;
  value: number;
  stage: string;
  source: string;
  assignedTo: string;
  createdDate: string;
}

interface LeadsData {
  config: {
    companyName: string;
    currentEBITDA: number;
    fy26Target: number;
  };
  summary: {
    totalValue: number;
    count: number;
    byStage: {
      prospect: number;
      qualified: number;
      proposal: number;
      negotiation: number;
    };
  };
  leads: Lead[];
}

export const LeadsDashboard: React.FC = () => {
  const [data, setData] = useState<LeadsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await api.get('/dashboard/leads');
        if (response.data.success) {
          setData(response.data.data);
        } else {
          setError('Failed to load leads data');
        }
      } catch (err) {
        setError('Failed to load leads data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-text-secondary">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-danger text-lg">{error || 'No data available'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background text-text">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="text-center mb-8 border-b-2 border-primary pb-6">
          <h1 className="text-4xl font-bold text-primary mb-2">Lead Generation Dashboard</h1>
          <p className="text-text-secondary text-lg">
            Pipeline Tracker - Path to {formatCompactCurrency(data.config.fy26Target)} EBITDA
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-primary font-semibold text-lg mb-2">Total Pipeline</h3>
            <p className="text-3xl font-bold text-primary">
              {formatCompactCurrency(data.summary.totalValue)}
            </p>
            <p className="text-text-secondary text-sm mt-1">{data.summary.count} leads</p>
          </div>

          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-primary font-semibold text-lg mb-2">Prospect</h3>
            <p className="text-3xl font-bold text-text">
              {data.summary.byStage.prospect}
            </p>
            <p className="text-text-secondary text-sm mt-1">Early stage</p>
          </div>

          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-primary font-semibold text-lg mb-2">Qualified</h3>
            <p className="text-3xl font-bold text-text">
              {data.summary.byStage.qualified}
            </p>
            <p className="text-text-secondary text-sm mt-1">Moving forward</p>
          </div>

          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-primary font-semibold text-lg mb-2">Proposal</h3>
            <p className="text-3xl font-bold text-text">
              {data.summary.byStage.proposal}
            </p>
            <p className="text-text-secondary text-sm mt-1">Near close</p>
          </div>
        </div>

        {/* Leads Table */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h2 className="text-2xl font-bold text-primary mb-4 border-b border-primary pb-2">
            All Leads
          </h2>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-primary">
                  <th className="text-left py-3 px-4 text-primary font-semibold">Company</th>
                  <th className="text-left py-3 px-4 text-primary font-semibold">Value</th>
                  <th className="text-left py-3 px-4 text-primary font-semibold">Stage</th>
                  <th className="text-left py-3 px-4 text-primary font-semibold">Source</th>
                  <th className="text-left py-3 px-4 text-primary font-semibold">Assigned To</th>
                  <th className="text-left py-3 px-4 text-primary font-semibold">Created</th>
                </tr>
              </thead>
              <tbody>
                {data.leads.map((lead) => (
                  <tr key={lead.id} className="border-b border-border hover:bg-background">
                    <td className="py-3 px-4">{lead.company}</td>
                    <td className="py-3 px-4 font-semibold">{formatCompactCurrency(lead.value)}</td>
                    <td className="py-3 px-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        lead.stage === 'Negotiation' ? 'bg-green-600 text-white' :
                        lead.stage === 'Proposal' ? 'bg-yellow-600 text-black' :
                        lead.stage === 'Qualified' ? 'bg-blue-600 text-white' :
                        'bg-gray-600 text-white'
                      }`}>
                        {lead.stage}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-text-secondary">{lead.source}</td>
                    <td className="py-3 px-4">{lead.assignedTo}</td>
                    <td className="py-3 px-4 text-text-secondary">{lead.createdDate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.leads.length === 0 && (
            <div className="text-center py-12 text-text-secondary">
              No leads found
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
